/* ==========================================================================
   Fashiongirl cart drawer — free-shipping progress + upsell rail

   Hooks the events Impulse already dispatches. theme.js is untouched:
     'cart:updated'  (detail.cart)  — fired after every quantity/remove change
     'ajaxProduct:added'            — fired after add to cart
     'cart:build'                   — asks Impulse to re-render the line items

   Upsell rows come from Shopify's Product Recommendations API, rendered by the
   'fg-cart-recommendations' section so prices and currency are server-formatted.
   The curated collection is only used when Shopify returns nothing.
   ========================================================================== */
(function () {
  'use strict';

  var SHIP = '[data-fg-ship]';
  var UPSELL = '[data-fg-upsell]';

  /* ---------- money ----------
     Formatted with Intl against the market's presentment currency and the page's
     own language, so Sweden gets "499 kr", France "4,00 €" and the Netherlands
     "€ 4,00". theme.settings.moneyFormat is deliberately not used here: it is the
     shop's format, which is wrong on every market that is not the primary one. */

  var lang = document.documentElement.getAttribute('lang') || 'en';
  var formatters = {};

  function money(cents, currency, forceComma) {
    var code = (currency || '').toUpperCase();
    // fashiongirl.eu is English-language but serves EUR to continental Europe,
    // which expects comma-decimal ("€26,01") - Intl('en', ...) gives US/UK-style
    // period-decimal instead. Ireland is also English + EUR but genuinely uses
    // period-decimal, so this is opt-in per page (data-force-comma) rather than
    // keyed off the currency or language alone.
    if (forceComma && code === 'EUR') {
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    }
    if (!code) {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
      return (cents / 100).toFixed(2);
    }
    if (!formatters[code]) {
      try {
        formatters[code] = new Intl.NumberFormat(lang, {
          style: 'currency',
          currency: code,
          minimumFractionDigits: cents % 100 === 0 ? 0 : 2
        });
      } catch (e) {
        formatters[code] = null;
      }
    }
    if (formatters[code]) return formatters[code].format(cents / 100);
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  /* ---------- free-shipping progress ---------- */

  function thresholdFor(el, currency) {
    var raw = el.getAttribute('data-thresholds') || '';
    var pairs = raw.split(',');
    var fallback = 0;

    for (var i = 0; i < pairs.length; i++) {
      var bits = pairs[i].split(':');
      if (bits.length < 2) continue;
      var code = bits[0].trim().toUpperCase();
      var value = parseFloat(bits[1].trim());
      if (isNaN(value)) continue;
      if (i === 0) fallback = value;
      if (code === currency) return value;
    }
    return fallback;
  }

  function renderShip(cart) {
    var el = document.querySelector(SHIP);
    if (!el) return;

    var currency = (cart && cart.currency) || (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
    currency = String(currency).toUpperCase();
    var forceComma = el.getAttribute('data-force-comma') === 'true';

    var threshold = thresholdFor(el, currency) * 100;
    if (!threshold) return;

    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var remaining = threshold - total;
    var reached = remaining <= 0;
    var pct = Math.min(100, (total / threshold) * 100);

    var text = el.querySelector('[data-fg-ship-message]') || el.querySelector('[data-fg-ship-text]');
    var fill = el.querySelector('[data-fg-ship-fill]');
    var track = el.querySelector('[data-fg-ship-track]');

    if (text) {
      text.innerHTML = reached
        ? (el.getAttribute('data-tpl-done') || '')
        : (el.getAttribute('data-tpl-progress') || '').replace('__AMT__', money(remaining, currency, forceComma));
    }
    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('fg-ship__fill--done', reached);
    }
    if (track) track.setAttribute('aria-valuenow', Math.round(pct));
    el.setAttribute('data-reached', reached);
  }

  /* ---------- upsell rail ---------- */

  var recCache = {};

  function trimList(list, inCart, limit) {
    var nodes = list.querySelectorAll('[data-fg-upsell-item]');
    var shown = 0;
    for (var i = 0; i < nodes.length; i++) {
      var id = parseInt(nodes[i].getAttribute('data-product-id'), 10);
      var eligible = !inCart[id] && shown < limit;
      nodes[i].hidden = !eligible;
      if (eligible) shown++;
    }
    return shown;
  }

  function renderUpsell(cart) {
    var root = document.querySelector(UPSELL);
    if (!root) return;

    var items = (cart && cart.items) || [];
    var inCart = {};
    for (var i = 0; i < items.length; i++) inCart[items[i].product_id] = true;

    var limit = parseInt(root.getAttribute('data-limit'), 10) || 3;
    var recBox = root.querySelector('[data-fg-upsell-recommended]');
    var fallback = root.querySelector('[data-fg-upsell-fallback]');

    if (!items.length) {
      root.hidden = true;
      return;
    }

    function settle() {
      var recList = recBox && recBox.querySelector('[data-fg-upsell-source="recommendations"]');
      var shown = 0;

      if (recList) {
        shown = trimList(recList, inCart, limit);
        recBox.hidden = shown === 0;
        if (fallback) fallback.hidden = shown > 0;
      }
      // Shopify had nothing useful: fall back to the curated collection
      if (!shown && fallback) {
        shown = trimList(fallback, inCart, limit);
        fallback.hidden = shown === 0;
        if (recBox) recBox.hidden = true;
      }
      root.hidden = shown === 0;
    }

    // Recommendations are keyed on the most recently added line, which is the
    // one the shopper is thinking about
    var seedId = items[items.length - 1].product_id;

    if (!recBox) {
      settle();
      return;
    }

    if (recCache[seedId] !== undefined) {
      recBox.innerHTML = recCache[seedId];
      settle();
      return;
    }

    var url = '/recommendations/products?section_id=fg-cart-recommendations' +
      '&product_id=' + seedId +
      '&limit=6&intent=related';

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('recommendations unavailable');
        return r.text();
      })
      .then(function (html) {
        var markup = '';
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var list = doc.querySelector('[data-fg-upsell-source="recommendations"]');
          if (list) markup = list.outerHTML;
        }
        // Only a successful response is cached. Caching a network failure would
        // suppress recommendations for this product for the rest of the session.
        recCache[seedId] = markup;
        recBox.innerHTML = markup;
        settle();
      })
      .catch(function () {
        recBox.innerHTML = '';
        settle();
      });
  }

  /* ---------- quick add ---------- */

  function quickAdd(button) {
    var id = button.getAttribute('data-variant-id');
    if (!id || button.hasAttribute('aria-busy')) return;

    button.setAttribute('aria-busy', 'true');
    button.textContent = button.getAttribute('data-label-adding') || '';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        button.textContent = button.getAttribute('data-label-added') || '';
        document.dispatchEvent(new CustomEvent('cart:build'));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {
        button.textContent = button.getAttribute('data-label-add') || '';
      })
      .then(function () {
        button.removeAttribute('aria-busy');
      });
  }

  /* ---------- wiring ---------- */

  function sync(cart) {
    renderShip(cart);
    renderUpsell(cart);
  }

  function refetch() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(sync)
      .catch(function () {});
  }

  document.addEventListener('cart:updated', function (evt) {
    if (evt.detail && evt.detail.cart) sync(evt.detail.cart);
    else refetch();
  });

  document.addEventListener('ajaxProduct:added', refetch);

  document.addEventListener('click', function (evt) {
    var button = evt.target.closest && evt.target.closest('[data-fg-upsell-add]');
    if (!button) return;
    evt.preventDefault();
    quickAdd(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refetch);
  } else {
    refetch();
  }
})();
